import { MYDRA_WORKSPACE_ID_HEADER_NAME } from '@/config/system'
import { ListPlaybooksOptions, PlaybooksService } from '@/services/playbook'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import { ApiErrorResponse, ApiPaginatedResponse } from '@/types/api'
import { ApiPlaybookListItem } from '@/types/playbook'
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'

export interface PlaybooksFilters {
  [key: string]: string | number | boolean | undefined
}

interface UsePlaybooksInfiniteOptions {
  filters?: PlaybooksFilters
  limit?: number
  queryOptions?: Omit<
    UseInfiniteQueryOptions<
      ListPlaybooksOptions,
      ApiErrorResponse,
      InfiniteData<ApiPaginatedResponse<ApiPlaybookListItem>>,
      readonly (string | number | boolean | PlaybooksFilters)[],
      number
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >
}

export const usePlaybooksInfinite = ({
  filters = {},
  limit = 12,
  queryOptions = {},
}: UsePlaybooksInfiniteOptions = {}) => {
  const { workspaceId } = useWorkspaceContext()
  return useInfiniteQuery({
    queryKey: [
      'playbooks',
      'infinite',
      filters,
      limit,
      ...(workspaceId ? ['workspaceId', workspaceId] : []),
    ],
    queryFn: ({ pageParam = 1 }) =>
      PlaybooksService.list({
        filters,
        pagination: { page: pageParam, limit },
        options: {
          headers: {
            [MYDRA_WORKSPACE_ID_HEADER_NAME]: workspaceId,
          },
        },
      }),
    getNextPageParam: (lastPage) => {
      const { metadata } = lastPage
      const hasNextPage = metadata.page * metadata.limit < metadata.total
      return hasNextPage ? metadata.page + 1 : undefined
    },
    initialPageParam: 1,
    ...queryOptions,
  })
}
